import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { IPageData, IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { NgInject } from '@df/ng/ng-inject';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { NBlogCategoryPageType } from '../../../cms/blog/page/category-view/cms-blog-page-category-view.component';
import { NBlogPostPageType } from '../../../cms/blog/page/post-view/cms-blog-post.namespace';
import { NBlogRootPageType } from '../../../cms/blog/page/root-view/cms-blog-root-page.namespace';
import { NInformationPageType } from '../../../cms/information/page/cms-information-page.component';
import { NInformationRootPageType } from '../../../cms/information/root-page/cms-information-root-page.component';
import { NPageStoreFinderPageType } from '../store-finder/page-store-finder.component';
import { LocalPageResource } from './local-page.resource';
import { TLoadDependeciesParams } from '@df/catalog/model';

export class LocalPage<T extends IPageData = IPageData> extends ICmsPage<T> {
    /**
     * Linked posts by tag
     * blog-category type only
     */
    private tagLinkedPosts?: LocalPage<IPageDataV2>[] | undefined;

    @NgInject(DfApiConfigService)
    private dfApiConfigService!: DfApiConfigService;

    get informationHeaderBlockVariant(): string {
        return this.data?.id + ' header-section';
    }

    get informationContentBlockVariant(): string {
        return this.data?.id + ' info-content';
    }

    protected override addDependenciesToLoader(params: TLoadDependeciesParams) {
        super.addDependenciesToLoader(params);
        const safeParams = { ...params, safe: true };
        if (this.verbosity! > 1) {
            if (this.rawData?.pageType === NBlogPostPageType.typeId) {
                // for blog post load all categories
                params.loader.add(this.loadBlogPostCategories());
            }
        }
        if (this.verbosity! > 2) {
            const pageResuorce = this.resources.page as any as LocalPageResource;
            switch (this.rawData?.pageType) {
                case NPageStoreFinderPageType.typeId:
                    params.loader.add(this.app.resources.store.loadAll(1, safeParams));
                    params.loader.add(this.dfApiConfigService.loadCountries());
                    break;
                case NBlogCategoryPageType.typeId:
                    // for blog category load all posts
                    params.loader.add(this.loadBlogCategoryPosts(safeParams));
                    // for blog category load all blog categories categories
                    params.loader.add(pageResuorce.loadBlogCategories(2, safeParams));
                    params.loader.add(this.loadBlogCategoryPosts(safeParams));
                    // for blog category load root page too
                    params.loader.add(pageResuorce.loadBlogRoot(2, safeParams));
                    break;
                case NBlogRootPageType.typeId:
                    // for blog category load all posts
                    params.loader.add(pageResuorce.loadBlogPosts(2, safeParams));
                    // for blog category load all blog categories categories
                    params.loader.add(pageResuorce.loadBlogCategories(2, safeParams));
                    break;
                case NInformationRootPageType.typeId:
                    // for information page load all information pages
                    params.loader.add(pageResuorce.loadInformationRootChildren(2, this.rawData?.id, safeParams));
                    break;
                case NInformationPageType.typeId:
                    if (this.rawData?.parent) {
                        params.loader.add(pageResuorce.load(this.rawData?.parent, 2, safeParams));
                        params.loader.add(pageResuorce.loadInformationRootChildren(2, this.rawData?.parent, safeParams));
                        params.loader.add(pageResuorce.loadInformationRootChildren(2, this.id, safeParams));
                    }
                    params.loader.add(pageResuorce.loadInformationHeaderBlock(this.informationHeaderBlockVariant, params));
                    params.loader.add(pageResuorce.loadInformationContentBlock(this.informationContentBlockVariant, params));
                    break;
            }
        }
    }

    /**
     * Loads categories (tags) of a blog post
     * blog-post type only
     */
    private loadBlogPostCategories(): Promise<LocalPage<IPageDataV2>[]> | void {
        const categoryIds = this.getBlogPostCategoryIds();
        if (categoryIds) {
            return this.resources.page.loadAllIds(categoryIds, 2, { safe: true }) as Promise<LocalPage<IPageDataV2>[]>;
        }
    }
    /**
     * Loads posts linked to a blog category (including linked by tags entry)
     * blog-category type only
     */

    private loadBlogCategoryPosts(params: TLoadDependeciesParams | undefined): Promise<unknown> | void {
        if (this.rawData?.pageType === NBlogCategoryPageType.typeId) {
            const tagLinkedPostsPromise = this.resources.page.find({ filter: { 'typeData.tags': this.id }, verbosity: 2 }, params);
            tagLinkedPostsPromise.then(pages => (this.tagLinkedPosts = pages as LocalPage<IPageDataV2>[]));
            return Promise.all([this.loadChildren(2, params), tagLinkedPostsPromise].filter(i => !!i));
        }
    }

    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     */
    getBlogPostCategories(): LocalPage<IPageDataV2>[] | undefined {
        const ids = this.getBlogPostCategoryIds();
        return ids && (this.resources.page.getAllExisting(ids) as LocalPage<IPageDataV2>[]);
    }
    /**
     * Retrieves categories (tags) ids of a blog post
     * blog-post type only
     */
    getBlogPostCategoryIds(): number[] {
        if (this.data?.pageType === NBlogPostPageType.typeId && this.verbosity > 1) {
            return uniq(flatten([this.data?.parent, (this.data as IPageDataV2).typeData?.tags]).filter(i => !!i));
        }
        return [];
    }
    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     * @returns
     */
    getBlogCategoryPosts(): LocalPage<IPageDataV2>[] {
        if (this.verbosity < 2) {
            return [];
        }
        switch (this.data?.pageType) {
            case NBlogCategoryPageType.typeId:
                return (uniq([...(this.children || []), ...(this.tagLinkedPosts || [])]) as LocalPage<IPageDataV2>[]).filter(
                    i => i.data?.pageType === NBlogPostPageType.typeId
                );
            case NBlogRootPageType.typeId:
                // for blogRoot get all blogposts with root as parent or grandparent
                return this.children?.length
                    ? uniq([
                          ...((this.children as LocalPage[]).filter(i => i.data?.pageType === NBlogPostPageType.typeId) || []),
                          ...flatten(this.children.map(child => (child as LocalPage).getBlogCategoryPosts()) || [])
                      ])
                    : [];
            default:
                return [];
        }
    }
}
